// MCHD Room Scheduler - Planner Store
// Zustand store for centralized planner state management with real-time updates
// Modified: 08-10-2025 11:27:08 - Added viewSyncEnabled, selectedCells, activeFilters

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { FilterChip, ConflictSuggestion, SelectedCell } from '../types/planner';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface PlannerFilters {
  startDate: Date;
  endDate: Date;
  buildingId: string | undefined;
  floorId: string | undefined;
  roomIds: string[];
  sessionTemplateIds: string[];
  userIds: string[];
  statuses: string[];
  includeBlocks: boolean;
  includeConflicts: boolean;
  includeUtilization: boolean;
}

interface UIState {
  activeView: 'grid' | 'list' | 'floor-plan';
  selectedDate: Date;
  selectedFloor?: string;
  selectedRoom?: string;
  sidePanelOpen: boolean;
  sidePanelContent: 'room-details' | 'conflicts' | 'utilization' | null;
  loading: boolean;
  error?: string;
  // NEW: View sync and filter state
  viewSyncEnabled: boolean; // Sync selection between Grid and Floor Plan
  selectedCells: SelectedCell[]; // Multi-select cells for bulk operations
  activeFilters: FilterChip[]; // Active filter chips for UI display
  conflictSuggestions: Map<string, ConflictSuggestion>; // Cached suggestions
  // PHASE 2: Sidebar collapsed state for layout
  sidebarCollapsed: boolean; // Whether navbar sidebar is collapsed to compact mode
  // PHASE 4: Legend visual filtering per Spec Section 3
  legendFilters: string[]; // Active legend filter IDs (visual-only, doesn't affect Grid)
  // Floor Plan edit mode state
  floorPlanEditMode: boolean; // Whether floor plan is in edit mode
}

interface AssignmentState {
  selectedAssignments: string[];
  bulkMode: boolean;
  conflictAssignments: string[];
  recentAssignments: Array<{
    id: string;
    roomId: string;
    sessionTemplateId: string;
    date: string;
    timestamp: number;
  }>;
}

interface PlannerState {
  // Filters
  filters: PlannerFilters;
  
  // UI State
  ui: UIState;
  
  // Assignment State
  assignments: AssignmentState;
  
  // Actions - Filters
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setBuildingId: (buildingId: string | undefined) => void;
  setFloorId: (floorId: string | undefined) => void;
  setRoomIds: (roomIds: string[]) => void;
  setSessionTimeIds: (sessionTemplateIds: string[]) => void;
  setUserIds: (userIds: string[]) => void;
  setStatuses: (statuses: string[]) => void;
  setIncludeBlocks: (include: boolean) => void;
  setIncludeConflicts: (include: boolean) => void;
  setIncludeUtilization: (include: boolean) => void;
  resetFilters: () => void;
  
  // Actions - UI State
  setActiveView: (view: 'grid' | 'list' | 'floor-plan') => void;
  setSelectedDate: (date: Date) => void;
  setSelectedFloor: (floorId: string | undefined) => void;
  setSelectedRoom: (roomId: string | undefined) => void;
  setSidePanelOpen: (open: boolean) => void;
  setSidePanelContent: (content: 'room-details' | 'conflicts' | 'utilization' | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | undefined) => void;
  setFloorPlanEditMode: (editMode: boolean) => void;
  // PHASE 2: Sidebar actions
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  // NEW: Modern 2025 UX Flow actions
  toggleViewSync: () => void;
  addSelectedCell: (cell: SelectedCell) => void;
  removeSelectedCell: (cellId: string) => void;
  clearSelectedCells: () => void;
  setSelectedCells: (cells: SelectedCell[]) => void;
  addFilterChip: (filter: FilterChip) => void;
  removeFilterChip: (filterId: string) => void;
  clearFilterChips: () => void;
  setConflictSuggestion: (cellId: string, suggestion: ConflictSuggestion) => void;
  getConflictSuggestion: (cellId: string) => ConflictSuggestion | undefined;
  // PHASE 4: Legend filter actions
  addLegendFilter: (statusId: string) => void;
  removeLegendFilter: (statusId: string) => void;
  clearLegendFilters: () => void;
  toggleLegendFilter: (statusId: string) => void;
  
  // Actions - Assignment State
  setSelectedAssignments: (assignments: string[]) => void;
  addSelectedAssignment: (assignmentId: string) => void;
  removeSelectedAssignment: (assignmentId: string) => void;
  clearSelectedAssignments: () => void;
  setBulkMode: (bulkMode: boolean) => void;
  setConflictAssignments: (conflicts: string[]) => void;
  addRecentAssignment: (assignment: {
    id: string;
    roomId: string;
    sessionTemplateId: string;
    date: string;
  }) => void;
  clearRecentAssignments: () => void;
  
  // Computed Values
  getFilterSummary: () => string;
  hasActiveFilters: () => boolean;
  getSelectedAssignmentCount: () => number;
}

// =============================================================================
// DEFAULT VALUES
// =============================================================================

const defaultFilters: PlannerFilters = {
  startDate: new Date(),
  endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Next 7 days
  buildingId: undefined, // EXPLICITLY set to undefined
  floorId: undefined,    // EXPLICITLY set to undefined
  roomIds: [],
  sessionTemplateIds: [],
  userIds: [],
  statuses: [],
  includeBlocks: true,
  includeConflicts: true,
  includeUtilization: false
};

const defaultUIState: UIState = {
  activeView: 'grid',
  selectedDate: new Date(),
  sidePanelOpen: false,
  sidePanelContent: null,
  loading: false,
  // PHASE 2: Sidebar defaults
  sidebarCollapsed: false,
  // NEW: Modern 2025 UX Flow defaults
  viewSyncEnabled: true,
  selectedCells: [],
  activeFilters: [],
  conflictSuggestions: new Map(),
  // PHASE 4: Legend filter defaults
  legendFilters: [],
  // Floor Plan edit mode default
  floorPlanEditMode: false
};

const defaultAssignmentState: AssignmentState = {
  selectedAssignments: [],
  bulkMode: false,
  conflictAssignments: [],
  recentAssignments: []
};

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const usePlannerStore = create<PlannerState>()(
  subscribeWithSelector((set, get) => ({
    // Initial State
    filters: defaultFilters,
    ui: defaultUIState,
    assignments: defaultAssignmentState,
    
    // Filter Actions
    setStartDate: (date: Date) =>
      set((state) => ({
        filters: { ...state.filters, startDate: date }
      })),
    
    setEndDate: (date: Date) =>
      set((state) => ({
        filters: { ...state.filters, endDate: date }
      })),
    
    setBuildingId: (buildingId: string | undefined) => {
      set((state) => {
        const newFilters = { 
          ...state.filters, 
          buildingId, // Directly assign (works with undefined)
          floorId: undefined, // Clear floor when building changes
          roomIds: [] // Clear rooms when building changes
        };
        return { filters: newFilters };
      });
    },
    
    setFloorId: (floorId: string | undefined) => {
      set((state) => {
        const newFilters = { 
          ...state.filters, 
          floorId, // Directly assign (works with undefined)
          roomIds: [] // Clear rooms when floor changes
        };
        return { filters: newFilters };
      });
    },
    
    setRoomIds: (roomIds: string[]) =>
      set((state) => ({
        filters: { ...state.filters, roomIds }
      })),
    
    setSessionTimeIds: (sessionTemplateIds: string[]) =>
      set((state) => ({
        filters: { ...state.filters, sessionTemplateIds }
      })),
    
    setUserIds: (userIds: string[]) =>
      set((state) => ({
        filters: { ...state.filters, userIds }
      })),
    
    setStatuses: (statuses: string[]) =>
      set((state) => ({
        filters: { ...state.filters, statuses }
      })),
    
    setIncludeBlocks: (include: boolean) =>
      set((state) => ({
        filters: { ...state.filters, includeBlocks: include }
      })),
    
    setIncludeConflicts: (include: boolean) =>
      set((state) => ({
        filters: { ...state.filters, includeConflicts: include }
      })),
    
    setIncludeUtilization: (include: boolean) =>
      set((state) => ({
        filters: { ...state.filters, includeUtilization: include }
      })),
    
    resetFilters: () => {
      set(() => ({
        filters: defaultFilters
      }));
    },
    
    // UI Actions
    setActiveView: (view: 'grid' | 'list' | 'floor-plan') =>
      set((state) => ({
        ui: { ...state.ui, activeView: view }
      })),
    
    setSelectedDate: (date: Date) =>
      set((state) => ({
        ui: { ...state.ui, selectedDate: date }
      })),
    
    setSelectedFloor: (floorId: string | undefined) => {
      set((state) => {
        const newUI = { ...state.ui } as any;
        if (floorId !== undefined) {
          newUI.selectedFloor = floorId;
        } else {
          delete newUI.selectedFloor;
        }
        return { ui: newUI as UIState };
      });
    },
    
    setSelectedRoom: (roomId: string | undefined) => {
      set((state) => {
        const newUI = { ...state.ui } as any;
        if (roomId !== undefined) {
          newUI.selectedRoom = roomId;
        } else {
          delete newUI.selectedRoom;
        }
        return { ui: newUI as UIState };
      });
    },
    
    setSidePanelOpen: (open: boolean) =>
      set((state) => ({
        ui: { ...state.ui, sidePanelOpen: open }
      })),
    
    setSidePanelContent: (content: 'room-details' | 'conflicts' | 'utilization' | null) =>
      set((state) => ({
        ui: { ...state.ui, sidePanelContent: content }
      })),
    
    setLoading: (loading: boolean) =>
      set((state) => ({
        ui: { ...state.ui, loading }
      })),
    
    setError: (error: string | undefined) => {
      set((state) => {
        const newUI = { ...state.ui } as any;
        if (error !== undefined) {
          newUI.error = error;
        } else {
          delete newUI.error;
        }
        return { ui: newUI as UIState };
      });
    },
    
    setFloorPlanEditMode: (editMode: boolean) =>
      set((state) => ({
        ui: { ...state.ui, floorPlanEditMode: editMode }
      })),
    
    // PHASE 2: Sidebar Actions
    toggleSidebarCollapsed: () =>
      set((state) => ({
        ui: { ...state.ui, sidebarCollapsed: !state.ui.sidebarCollapsed }
      })),
    
    setSidebarCollapsed: (collapsed: boolean) =>
      set((state) => ({
        ui: { ...state.ui, sidebarCollapsed: collapsed }
      })),
    
    // NEW: Modern 2025 UX Flow Actions
    toggleViewSync: () =>
      set((state) => ({
        ui: { ...state.ui, viewSyncEnabled: !state.ui.viewSyncEnabled }
      })),
    
    addSelectedCell: (cell: SelectedCell) =>
      set((state) => ({
        ui: {
          ...state.ui,
          selectedCells: [...state.ui.selectedCells, cell]
        }
      })),
    
    removeSelectedCell: (cellId: string) =>
      set((state) => ({
        ui: {
          ...state.ui,
          selectedCells: state.ui.selectedCells.filter(cell => cell.id !== cellId)
        }
      })),
    
    clearSelectedCells: () =>
      set((state) => ({
        ui: { ...state.ui, selectedCells: [] }
      })),
    
    setSelectedCells: (cells: SelectedCell[]) =>
      set((state) => ({
        ui: { ...state.ui, selectedCells: cells }
      })),
    
    addFilterChip: (filter: FilterChip) =>
      set((state) => ({
        ui: {
          ...state.ui,
          activeFilters: [...state.ui.activeFilters, filter]
        }
      })),
    
    removeFilterChip: (filterId: string) =>
      set((state) => ({
        ui: {
          ...state.ui,
          activeFilters: state.ui.activeFilters.filter(f => f.id !== filterId)
        }
      })),
    
    clearFilterChips: () =>
      set((state) => ({
        ui: { ...state.ui, activeFilters: [] }
      })),
    
    setConflictSuggestion: (cellId: string, suggestion: ConflictSuggestion) =>
      set((state) => ({
        ui: {
          ...state.ui,
          conflictSuggestions: new Map(state.ui.conflictSuggestions).set(cellId, suggestion)
        }
      })),
    
    getConflictSuggestion: (cellId: string) => {
      const state = get();
      return state.ui.conflictSuggestions.get(cellId);
    },
    
    // PHASE 4: Legend Filter Actions
    addLegendFilter: (statusId: string) =>
      set((state) => ({
        ui: {
          ...state.ui,
          legendFilters: [...state.ui.legendFilters, statusId]
        }
      })),
    
    removeLegendFilter: (statusId: string) =>
      set((state) => ({
        ui: {
          ...state.ui,
          legendFilters: state.ui.legendFilters.filter(id => id !== statusId)
        }
      })),
    
    clearLegendFilters: () =>
      set((state) => ({
        ui: { ...state.ui, legendFilters: [] }
      })),
    
    toggleLegendFilter: (statusId: string) =>
      set((state) => ({
        ui: {
          ...state.ui,
          legendFilters: state.ui.legendFilters.includes(statusId)
            ? state.ui.legendFilters.filter(id => id !== statusId)
            : [...state.ui.legendFilters, statusId]
        }
      })),
    
    // Assignment Actions
    setSelectedAssignments: (assignments: string[]) =>
      set((state) => ({
        assignments: { ...state.assignments, selectedAssignments: assignments }
      })),
    
    addSelectedAssignment: (assignmentId: string) =>
      set((state) => ({
        assignments: {
          ...state.assignments,
          selectedAssignments: [...state.assignments.selectedAssignments, assignmentId]
        }
      })),
    
    removeSelectedAssignment: (assignmentId: string) =>
      set((state) => ({
        assignments: {
          ...state.assignments,
          selectedAssignments: state.assignments.selectedAssignments.filter(id => id !== assignmentId)
        }
      })),
    
    clearSelectedAssignments: () =>
      set((state) => ({
        assignments: { ...state.assignments, selectedAssignments: [] }
      })),
    
    setBulkMode: (bulkMode: boolean) =>
      set((state) => ({
        assignments: { ...state.assignments, bulkMode }
      })),
    
    setConflictAssignments: (conflicts: string[]) =>
      set((state) => ({
        assignments: { ...state.assignments, conflictAssignments: conflicts }
      })),
    
    addRecentAssignment: (assignment) =>
      set((state) => ({
        assignments: {
          ...state.assignments,
          recentAssignments: [
            { ...assignment, timestamp: Date.now() },
            ...state.assignments.recentAssignments.slice(0, 9) // Keep only last 10
          ]
        }
      })),
    
    clearRecentAssignments: () =>
      set((state) => ({
        assignments: { ...state.assignments, recentAssignments: [] }
      })),
    
    // Computed Values
    getFilterSummary: () => {
      const state = get();
      const { filters } = state;
      const parts: string[] = [];
      
      if (filters.buildingId) parts.push(`Building: ${filters.buildingId}`);
      if (filters.floorId) parts.push(`Floor: ${filters.floorId}`);
      if (filters.roomIds.length > 0) parts.push(`Rooms: ${filters.roomIds.length}`);
      if (filters.sessionTemplateIds.length > 0) parts.push(`Sessions: ${filters.sessionTemplateIds.length}`);
      if (filters.userIds.length > 0) parts.push(`Users: ${filters.userIds.length}`);
      if (filters.statuses.length > 0) parts.push(`Status: ${filters.statuses.join(', ')}`);
      
      return parts.length > 0 ? parts.join(' • ') : 'No filters applied';
    },
    
    hasActiveFilters: () => {
      const state = get();
      const { filters } = state;
      
      return !!(
        filters.buildingId ||
        filters.floorId ||
        filters.roomIds.length > 0 ||
        filters.sessionTemplateIds.length > 0 ||
        filters.userIds.length > 0 ||
        filters.statuses.length > 0
      );
    },
    
    getSelectedAssignmentCount: () => {
      const state = get();
      return state.assignments.selectedAssignments.length;
    }
  }))
);

// =============================================================================
// SELECTORS
// =============================================================================

export const usePlannerFilters = () => usePlannerStore((state) => state.filters);
export const usePlannerUI = () => usePlannerStore((state) => state.ui);
export const usePlannerAssignments = () => usePlannerStore((state) => state.assignments);

export const useActiveFilters = () => usePlannerStore((state) => ({
  filters: state.filters,
  hasActive: state.hasActiveFilters(),
  summary: state.getFilterSummary()
}));

export const useSelectedAssignments = () => usePlannerStore((state) => ({
  selected: state.assignments.selectedAssignments,
  count: state.getSelectedAssignmentCount(),
  bulkMode: state.assignments.bulkMode
}));

// =============================================================================
// SUBSCRIPTIONS
// =============================================================================

// Subscribe to filter changes for analytics/logging
usePlannerStore.subscribe(
  (state) => state.filters,
  (filters) => {
    // Log filter changes for analytics
    console.log('Planner filters changed:', filters);
  }
);

// Subscribe to view changes for URL updates
usePlannerStore.subscribe(
  (state) => state.ui.activeView,
  (activeView) => {
    // Update URL when view changes
    const url = new URL(window.location.href);
    url.searchParams.set('view', activeView);
    window.history.replaceState({}, '', url.toString());
  }
);

// Subscribe to selected date changes
usePlannerStore.subscribe(
  (state) => state.ui.selectedDate,
  (selectedDate) => {
    // Update URL when date changes
    const url = new URL(window.location.href);
    url.searchParams.set('date', selectedDate.toISOString().split('T')[0]);
    window.history.replaceState({}, '', url.toString());
  }
);

// Subscribe to filter changes for URL persistence
usePlannerStore.subscribe(
  (state) => state.filters,
  (filters) => {
    const url = new URL(window.location.href);
    
    // Building and floor IDs
    if (filters.buildingId) {
      url.searchParams.set('building', filters.buildingId);
    } else {
      url.searchParams.delete('building');
    }
    
    if (filters.floorId) {
      url.searchParams.set('floor', filters.floorId);
    } else {
      url.searchParams.delete('floor');
    }
    
    // Array filters (rooms, sessions, users)
    if (filters.roomIds.length > 0) {
      url.searchParams.set('rooms', filters.roomIds.join(','));
    } else {
      url.searchParams.delete('rooms');
    }
    
    if (filters.sessionTemplateIds.length > 0) {
      url.searchParams.set('sessions', filters.sessionTemplateIds.join(','));
    } else {
      url.searchParams.delete('sessions');
    }
    
    if (filters.userIds.length > 0) {
      url.searchParams.set('users', filters.userIds.join(','));
    } else {
      url.searchParams.delete('users');
    }
    
    if (filters.statuses.length > 0) {
      url.searchParams.set('statuses', filters.statuses.join(','));
    } else {
      url.searchParams.delete('statuses');
    }
    
    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());
  }
);
